import * as React from "react";
import styled from "@emotion/styled";
import classNames from "classnames";
import ReactSelectBox from "react-select";
import InputText, { InputTextProps } from "@/components/atoms/InputText";
import SelectBox, { SelectBoxProps } from "@/components/atoms/SelectBox";
import InputRadio, { InputRadioProps } from "@/components/atoms/InputRadio";
import theme from "@/theme";
import { isBlank, isReactNodeArray } from "@/utils/is";
import SearchMansion from "@/components/organisms/SearchMansion";
import breakpoint from "@/utils/breakpoint";
import Checkbox, { CheckboxProps } from "@/components/atoms/Checkbox";
import { isColorCode } from "@/utils/color";
import { SearchMansionResponse } from "@/apis/property";
import Land from "@/assets/icons/Land";
import Mansion from "@/assets/icons/Mansion";
import House from "@/assets/icons/House";
import PropertyType from "@/constants/PropertyType";
import { useTheme } from "@/hooks/useTheme";
import MediaQueryContext, { initMediaQueryStore } from "@/contexts/MediaQuery";
import questionMarkCircleIcon from "@/assets/icons/question-mark-circle.svg";

type FormBlockProps = {
  width?: string;
  height?: string;
  hidden?: boolean;
  backgroundColor?: string;
  children: (React.ReactElement<FormLabelProps> | React.ReactElement<FormBlockRowProps>)[];
} & React.HTMLAttributes<HTMLDivElement>;

type FormBlockElementType =
  | React.ReactElement<FormInputProps>
  | React.ReactElement<SelectBoxProps>
  | React.ReactElement<FormRadioProps>;

type FormBlockRowProps = {
  hidden?: boolean;
  children: FormBlockElementType | FormBlockElementType[];
} & React.HtmlHTMLAttributes<HTMLDivElement>;

type FormLabelProps = {
  value: string;
  required?: boolean;
  background?: string | undefined;
  text?: string | undefined;
  border?: string | undefined;
  borderRadius?: string | undefined;
  labelBackgroundColor?: string;
  modalData?: string;
  onInfoIconClick?: (value: string) => void;
} & React.LabelHTMLAttributes<HTMLLabelElement>;

type FormInputProps = {
  description?: string;
  before?: string;
  after?: string;
  search?: boolean;
  disabledOver?: boolean;
  validateMessage?: string;
  required?: boolean;
  background?: string | undefined;
  placeHolderColor?: string | undefined;
  boarderRadius?: string | undefined;
  borderColor?: string | undefined;
  isInfoText?: boolean;
  inputWrapperClassName?: string;
  innerInputClassName?: string;
  selectMansion?: (mansion: SearchMansionResponse) => void;
} & InputTextProps;

type FormSelectProps = {
  description?: string | JSX.Element;
  disabledOver?: boolean;
  validateMessage?: string;
  required?: boolean;
  background?: string | undefined;
  wrapperClassName?: string;
} & SelectBoxProps;

type FormRadioProps = {
  description?: string;
  disabledOver?: boolean;
  validateMessage?: string;
  inline?: boolean;
} & InputRadioProps;

type FormCheckboxProps = {
  description?: string;
  descriptionColor?: string;
  disabledOver?: boolean;
  validateMessage?: string;
  inline?: boolean;
  isBorder?: boolean;
  maxWidth?:string;
  fontWeight?: string;
  fontSize?: string;
} & CheckboxProps;

const Wrapper = styled.div<Pick<FormBlockProps, "width" | "height" | "backgroundColor">>`
  display: flex;
  width: ${({ width = "100%" }) => width};
  height: ${({ height = "auto" }) => height};

  &.center {
    .label-container {
      align-items: center;
    }
  }

  &.disabled {
    .body-container {
      input {
        border-color: transparent;
        background: transparent;
        color: #666;
        opacity: 1;
      }
    }
  }

  &.readOnly {
    .body-container {
      input {
        border-color: transparent;
        background: transparent;
        color: #333333;
        opacity: 1;
      }
    }
  }

  &.underline {
    border-bottom: 1px solid #ccc;

    &:last-of-type: {
      border-bottom: none;
    }
  }

  &.valuation {
    border: none;
    .body-container {
      background: white;
      .override {
        font-size: 14px !important;
        font-weight: 500 !important;
      }
      .fontChanged {
        font-size: 12px !important;
        font-weight: 400 !important;
      }
      input {
        height: 45px !important;
      }
    }
    .label-container {
      padding: 0 20px;
      background: ${({ backgroundColor = "#43A0DF" }) => backgroundColor};
      .override {
        font-size: 14px !important;
        font-weight: 500 !important;
      }
    }
  }

  &.defaultDataTable {
    .label-container {
      background: #f2f2f2;
    }
  }

  &.dataTable {
    border: none;
    .body-container {
      padding: 5px 15px;
      background: white;
      .override {
        font-size: 14px !important;
        font-weight: 400 !important;
        color: #333333 !important;
      }
    }
    .label-container {
      padding: 5px 30px;
      border: none;
      background: ${({ backgroundColor = "#43A0DF" }) => backgroundColor};
      .override {
        font-size: 14px !important;
        font-weight: 500 !important;
        color: #333333 !important;
      }
    }
    ${breakpoint.lessThan("sm")`
    .body-container {
      padding: 0 10px;    
      input {
        padding: 0;
      }
    }
    .label-container {
      padding: 5px 10px;    
      height: 30px;
    }
  `}
  }

  &.form {
    border: none;
    .body-container {
      background: white;
      padding: 22px 15px;
      .override {
        font-size: 14px !important;
        font-weight: 500 !important;
        color: #333333;
      }
      .fontChanged {
        font-size: 12px !important;
        font-weight: 400 !important;
      }
    }
    .label-container {
      font-size: 16px !important;
      font-weight: 700 !important;
      background: ${({ backgroundColor = "#43A0DF" }) => backgroundColor};
      min-width: 194px;
      padding: 22px 15px;
      label {
        color: #333333;
      }
    }
    ${breakpoint.lessThan("sm")`
    background: white;
    .label-container {
      padding: 15px 0;
      align-items: center;
      .override {
        display: flex;
        align-items: center;
      }
    }
    .body-container {
      padding: 15px 0;
    }
  `}
  }

  ${breakpoint.lessThan("sm")`
    flex-direction: column;
  `}
`;

const LabelContainer = styled.div<{ bgColor?: string }>`
  display: flex;
  align-items: flex-start;
  justify-content: left;
  background: #f2f2f2;
  padding: 16px 20px;
  min-width: 260px;
  height: auto;

  ${breakpoint.lessThan("sm")`
    height: 38px;
    padding: 6px 10px;
    border-bottom: 1px solid #C2C2C2;
  `}
  &.bgColor {
    background: ${({ bgColor = "#ffffff" }) => bgColor};
  }
`;

const BodyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 15px;

  ${breakpoint.lessThan("sm")`
    padding: 15px;
  `}
`;

const FormLabel = styled.label`
  font-size: 17px;
  font-weight: bold;
  color: #524939;
  &.override {
    font-weight: 700;
    font-size: 16px;
  }
`;

const InputWrapper = styled.div<{ width?: string }>`
  position: relative;
  width: ${({ width = "100%" }) => width};
  margin: 0 20px 0 0;

  &:last-of-type {
    margin: 0;
  }

  &.center {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  &.nameField {
    margin: 0 10px 0 0;
    width: 200px !important;
  }

  ${breakpoint.lessThan("sm")`
    width: 100% !important;
    margin: 0 0 10px;
  `}
  &.marginLeft10 {
    margin-right: 10px;
  }
  &.widthAuto {
    width: auto !important;
  }
  &.fieldWidth {
    margin: 0 15px 0 0;
    width: 104px !important;
  }
`;

const FormInput = styled(InputText)<{
  background?: string;
}>`
  font-size: 17px;
  font-weight: bold;
  color: #524939;
  align-self: center;
  position: relative;

  &.warn {
    background: ${({ background = "#f6e58d" }) => background};
    border: 1px solid ${theme.main.warning};
  }

  &.required {
    background: ${({ background = "#f6e58d" }) => background};
  }

  &:disabled {
    opacity: 0.5;
  }

  &.over {
    border-color: #008bac;
    opacity: 0.3;
    background: #eee;
  }
`;

const CssOverrideFormInput = styled(InputText)<{
  primaryColor?: string;
  secondaryColor?: string | null;
}>`
  font-size: 14px;
  font-weight: 400;
  height: 40px;
  align-self: center;
  position: relative;
  border: 1px solid #cccccc;
  border-radius: 5px;
  color: #333333;
  opacity: 1;
  padding: 10px 15px;
  &.padding: 10px 20px;

  ::placeholder {
    color: #cccccc;
    font-size: 14px;
    font-weight: 400;
  }
  &.warn {
    background: #fae6e6 !important;
  }

  &.required {
    background: ${({ secondaryColor = "#43A0DF" }) => secondaryColor};
  }

  &:disabled {
    opacity: 0.5;
  }

  &.over {
    border-color: #008bac;
    opacity: 0.3;
    background: #eee;
  }
  &.inputCss {
    width: 400px;
  }
  &.inputCss2 {
    width: 200px !important;
  }

  &.noHeight {
    height: auto;
  }

  &.width200px {
    width: 200px;
  }

  ${breakpoint.lessThan("sm")`
    &.inputCss {
      width: 100%;
     }
     &.inputCss2 {
      width: 200px;
    }
    &.inputCss3 {
      width: 80px;
    }
    &.inputCss4 {
      width: 100px;
    }
    
    &.inputCss5 {
      width: 165px;
    }
    &.width138px {
      width: 138px;
    }
    &.width100px {
      width: 100px;
    }
    &.width124px {
      width: 124px;
    }
    &.width104px {
      width: 104px;
    }
    &.width80px {
      width: 80px;
    }
    &.width224px {
      width: 224px;
    }
    &.width200px {
      width: 200px;
    }
  `}
`;

const FormSelect = styled(SelectBox)<{ background?: string }>`
  font-size: 17px;
  font-weight: bold;
  color: #524939;
  align-self: flex-start;
  position: relative;

  &.warn {
    background: ${({ background = "#f6e58d" }) => background};
    border: 1px solid ${theme.main.warning};
  }

  &.required {
    background: ${({ background = "#f6e58d" }) => background};
  }

  &:disabled {
    opacity: 0.5;
  }

  &.over {
    border-color: #008bac;
    opacity: 0.3;
    background: #eee;
  }
`;

const CssOverrideFormSelect = styled(SelectBox)<{
  background?: string;
  primaryColor?: string;
  secondaryColor?: string | null;
}>`
  font-size: 14px;
  font-weight: 400;
  color: #333333;
  height: 40px;
  align-self: flex-start;
  position: relative;
  border: 0;
  outline: 1px solid #cccccc;
  border-right: 15px solid transparent;
  padding 10px 15px;
  option {
    background-color: white;
  }
  &.warn {
    background: #fae6e6 !important;
  }

  &.required {
    background: ${({ secondaryColor = "#f6e58d" }) => secondaryColor};
  }

  &:disabled {
    opacity: 0.5;
  }

  &.over {
    opacity: 0.3;
    background: #eee;
  }
  &.selectCss {
    width: 200px !important;
  }
  &.selectCss2 {
    width: 108px !important;
  }
  &.selectCss3 {
    width: 100%;
  }
  &.width100Per {
    width: 100%;
  }
  &.width200px {
    width: 200px !important;
  }
`;

const Description = styled.p<{ descriptionColor?: string, maxWidth?: string, fontWeight?: string, fontSize?: string }>`
  color: ${({ descriptionColor }) => descriptionColor || "#666"};
  font-size: 14px;
  font-weight: bold;
  white-space: pre-wrap;
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  &.override {
    font-size: ${({ fontSize }) => fontSize || "14px"};
    font-weight: ${({ fontWeight }) => fontWeight || "500"};
    margin-bottom: 5px;
    line-height: 1.542;
    }
  &.fontChanged {
    font-size: 12px !important;
    margin-bottom: 5px;
  }
  &.checkbox2 {
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #333333;
  }
  &.regular {
    font-weight: 400 !important;
  }
  &.addWidth {
    width: 50%;
    ${breakpoint.lessThan("sm")`
    width: 100%;
  `}
  }
`;

const InputInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Before = styled.p`
  color: #666;
  font-size: 14px;
  font-weight: bold;
  margin-right: 10px;
  position: relative;
  top: 3px;
  white-space: nowrap;

  &.override {
    position: absolute;
  }

  ${breakpoint.lessThan("sm")`
    font-size: 12px;
    font-weight: normal;
  `}
`;

const After = styled.p`
  color: #666;
  font-size: 14px;
  font-weight: bold;
  margin-left: 10px;
  position: relative;
  top: 3px;
  white-space: nowrap;

  ${breakpoint.lessThan("sm")`
    font-size: 12px;
    margin-right: 20px;
    font-weight: normal;
  `}
  &.override {
    font-size: 14px;
    font-weight: 500;
  }
`;

const NeedSpan = styled.span<{ background?: string; text?: string; border?: string }>`
  font-size: 15px;
  color: ${({ text = theme.main.white }) => text};
  background: ${({ background = "#e22929" }) => background};
  margin: 0 0 0 5px;
  padding: 0 0 0 4px;
  border: 2px solid ${({ border = "#e22929" }) => border};
  width: 42px;
  line-height: 20px;
  display: inline-block;
  &.override {
    margin: 5px 0 0 0;
    font-size: 10px;
    display: block;
    font-weight: 700;
    border-radius: 100px;
    text-align: center;
    padding: 0;
    width: 37px;
    height: 17px;
    line-height: 1.3;
    border: 1px solid ${({ border = "#e22929" }) => border};
  }
  &.borderRadius {
    font-size: 10px;
    font-weight: 700;
    border-radius: 100px;
    text-align: center;
    padding: 0;
    width: 37px;
    height: 17px;
    line-height: 1.3;
    border: 1px solid ${({ border = "#e22929" }) => border};
  }
`;

const WarnMessage = styled.p`
  font-size: 15px;
  color: ${theme.main.warning};
  width: 135px;
  white-space: nowrap;
  &.msgOverride {
    margin-top: 8px;
    color: #ff7979;
    font-weight: 400;
    font-size: 12px;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  margin: 0 0 15px;

  &:last-of-type {
    margin: 0;
  }

  &.displayRow {
    flex-direction: row;
  }
  &.displayColumn {
    flex-direction: column;
  }
  &.flexStart {
    justify-content: flex-start;
    .css-1dhwk8m-InputWrapper {
      margin-right: 10px;
    }
  }
  ${breakpoint.lessThan("sm")`
    flex-direction: column;
  `}
`;

const FormBlock = ({ children, ...props }: FormBlockProps) => {
  const Label = isReactNodeArray(children) ? children.find(x => x?.type === FormBlock.Label) : children;
  const Other = isReactNodeArray(children) ? children.filter(x => x?.type !== FormBlock.Label) : [];
  return (
    <>
      {!props.hidden && (
        <Wrapper {...props}>
          <LabelContainer className="label-container">{Label}</LabelContainer>
          <BodyContainer className="body-container">{Other}</BodyContainer>
        </Wrapper>
      )}
    </>
  );
};

const StyledIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 6px;
  vertical-align: middle;
  ${breakpoint.lessThan("sm")`
  margin-left: auto;
`}
`;

FormBlock.Label = ({
  value,
  required = false,
  background = theme.main.white,
  text = "#e22929",
  border = "#e22929",
  borderRadius = "0",
  labelBackgroundColor = "#ffffff",
  modalData = undefined,
  ...props
}: FormLabelProps) => {
  const { isNewTheme, COLOR_THEME } = useTheme();
  const { current: range } = React.useContext(MediaQueryContext) || initMediaQueryStore("md");

  const renderNeedSpan = (textStr: string, color: string) => {
    if (isNewTheme) {
      if (textStr === "任意") {
        return (
          <NeedSpan
            background={isColorCode(background) ? background : theme.main.white}
            text={isColorCode(color) ? color : "#43A0DF"}
            border={isColorCode(color) ? color : "#43A0DF"}
            className={classNames({
              override: range !== "sm" && isNewTheme,
              borderRadius: range === "sm" && isNewTheme,
            })}
          >
            {textStr}
          </NeedSpan>
        );
      } else {
        return (
          <NeedSpan
            background={
              COLOR_THEME.required_icon && isColorCode(COLOR_THEME.required_icon.background as string)
                ? COLOR_THEME.required_icon.background
                : theme.main.white
            }
            text={
              COLOR_THEME.required_icon && isColorCode(COLOR_THEME.required_icon.text as string)
                ? COLOR_THEME.required_icon.text
                : COLOR_THEME.extras?.secondary
            }
            border={
              COLOR_THEME.required_icon && isColorCode(COLOR_THEME.required_icon.border as string)
                ? COLOR_THEME.required_icon.border
                : COLOR_THEME.extras?.secondary
            }
            className={classNames({
              override: range !== "sm" && isNewTheme,
              borderRadius: range === "sm" && isNewTheme,
            })}
          >
            {textStr}
          </NeedSpan>
        );
      }
    } else {
      return (
        <NeedSpan
          background={isColorCode(background) ? background : theme.main.white}
          text={isColorCode(text) ? text : "#e22929"}
          border={isColorCode(border) ? border : "#e22929"}
        >
          {textStr}
        </NeedSpan>
      );
    }
  };

  const renderLabelContent = () => {
    if (isNewTheme) {
      if (modalData && range !== "sm") {
        return (
          <React.Fragment>
            <StyledIcon
              src={questionMarkCircleIcon}
              onClick={() => props.onInfoIconClick && props.onInfoIconClick(modalData)}
            />
            {required && renderNeedSpan("必須", COLOR_THEME.extras?.secondary as string)}
            {!required && isNewTheme && renderNeedSpan("任意", "#CCCCCC")}
          </React.Fragment>
        );
      } else if (modalData && range === "sm") {
        return (
          <React.Fragment>
            {required && renderNeedSpan("必須", COLOR_THEME.extras?.secondary as string)}
            {!required && isNewTheme && renderNeedSpan("任意", "#CCCCCC")}
            <StyledIcon
              src={questionMarkCircleIcon}
              onClick={() => props.onInfoIconClick && props.onInfoIconClick(modalData)}
            />
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment>
            {required && renderNeedSpan("必須", COLOR_THEME.extras?.secondary as string)}
            {!required && isNewTheme && renderNeedSpan("任意", "#CCCCCC")}
          </React.Fragment>
        );
      }
    } else {
      return (
        <React.Fragment>{required && renderNeedSpan("必須", COLOR_THEME.extras?.secondary as string)}</React.Fragment>
      );
    }
  };

  return (
    <FormLabel
      {...props}
      className={classNames({
        override: isNewTheme,
      })}
    >
      {value}
      {renderLabelContent()}
    </FormLabel>
  );
};

FormBlock.Input = ({
  description,
  before,
  after,
  disabledOver = false,
  search = false,
  value = "",
  width,
  validateMessage,
  required,
  background = "#f6e58d",
  boarderRadius = "4px",
  borderColor = "#CCCCCC",
  isInfoText = false,
  selectMansion,
  inputWrapperClassName,
  innerInputClassName,
  ...props
}: FormInputProps) => {
  const { isNewTheme, COLOR_THEME } = useTheme();
  let placeholder = props.placeholder;
  if (placeholder && isNewTheme) placeholder = placeholder.replace("例）", "例 ：");
  const renderInputField = () => {
    if (isNewTheme) {
      return (
        <CssOverrideFormInput
          value={value}
          {...props}
          className={classNames({
            over: disabledOver && props.disabled,
            required: required && isBlank(value),
            warn: !isBlank(validateMessage),
            [props.className as string]: true,
          })}
          primaryColor={COLOR_THEME.extras?.primary}
          secondaryColor={COLOR_THEME.extras?.validation}
          placeholder={placeholder}
        />
      );
    } else {
      return (
        <FormInput
          value={value}
          background={isColorCode(background) ? background : "#f6e58d"}
          {...props}
          className={classNames({
            over: disabledOver && props.disabled,
            required: required && isBlank(value),
            warn: !isBlank(validateMessage),
            [props.className as string]: true,
          })}
        />
      );
    }
  };

  if (isNewTheme && before) {
    description = isNewTheme ? before : description;
    before = undefined;
  }

  return (
    <InputWrapper width={width} className={inputWrapperClassName}>
      <Description
        className={classNames({
          override: isNewTheme,
        })}
      >
        {description}
      </Description>
      <InputInner className={innerInputClassName}>
        {before && <Before>{before}</Before>}
        {renderInputField()}
        {after && (
          <After
            className={classNames({
              override: isNewTheme,
            })}
          >
            {after}
          </After>
        )}
        {search && <SearchMansion propertyName={value} selectMansion={selectMansion} />}
      </InputInner>
      {validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>{validateMessage}</WarnMessage>
      )}
    </InputWrapper>
  );
};

FormBlock.ReactSelect = ({ ...props }) => {
  const { COLOR_THEME, isNewTheme } = useTheme();
  return (
    <InputWrapper width={props.width} className={props.wrapperClassName}>
      <Description
        className={classNames({
          override: isNewTheme,
        })}
      >
        {props.description}
      </Description>
      <ReactSelectBox
        placeholder={props.placeholder}
        value={props.value}
        options={props.options}
        noOptionsMessage={() => "候補がありません"}
        components={{
          IndicatorSeparator: () => null,
        }}
        isSearchable={props.isSearchable}
        isDisabled={props.isDisabled}
        onChange={props.onChange}
        styles={{
          container: styles => {
            return {
              ...styles,
              borderColor: "#cccccc",
              "&:hover": {
                borderColor: "#cccccc",
              },
            };
          },
          singleValue: base => ({
            ...base,
            padding: 0,
          }),
          control: (styles, { isDisabled }) => {
            return {
              ...styles,
              width: props.width,
              height: props.height,
              marginRight: "20px",
              fontSize: "14px",
              lineHeight: "inherit",
              color: "#333333",
              backgroundColor: props.value ? "#ffffff" : COLOR_THEME.extras?.validation,
              opacity: isDisabled ? 0.5 : 1,
              border: "1px solid #cccccc",
              boxShadow: "none",
              "&:hover": {
                border: "1px solid #cccccc",
              },
            };
          },

          option: styles => {
            return {
              ...styles,
              backgroundColor: "white",
              color: "#333333",
              fontSize: "14px",
              lineHeight: "inherit",
              padding: "1px 10px",
              "&:hover": {
                backgroundColor: "#2196F3",
                color: "white",
              },
            };
          },
          dropdownIndicator: base => ({
            ...base,
            color: "#333333", // Custom colour
            paddingRight: "15px",
            width: "39px",
            height: "auto",
          }),
        }}
      />
      {props.validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>
          {props.validateMessage}
        </WarnMessage>
      )}
    </InputWrapper>
  );
};

FormBlock.Select = ({
  description,
  disabledOver = false,
  validateMessage,
  value = "",
  width,
  required,
  background = "#f6e58d",
  ...props
}: FormSelectProps) => {
  const { isNewTheme, COLOR_THEME } = useTheme();
  const renderFormSelect = () => {
    if (isNewTheme) {
      return (
        <CssOverrideFormSelect
          value={value}
          background={isColorCode(background) ? background : "#f6e58d"}
          {...props}
          className={classNames({
            warn: !isBlank(validateMessage),
            over: disabledOver && props.disabled,
            required: required && isBlank(value),
            [props.className as string]: true,
          })}
          primaryColor={COLOR_THEME.extras?.primary}
          secondaryColor={COLOR_THEME.extras?.validation}
        />
      );
    } else {
      return (
        <FormSelect
          value={value}
          background={isColorCode(background) ? background : "#f6e58d"}
          {...props}
          className={classNames({
            warn: !isBlank(validateMessage),
            over: disabledOver && props.disabled,
            required: required && isBlank(value),
            [props.className as string]: true,
          })}
        />
      );
    }
  };
  return (
    <InputWrapper width={width} className={props.wrapperClassName}>
      <Description
        className={classNames({
          override: isNewTheme,
        })}
      >
        {description}
      </Description>
      {renderFormSelect()}
      {validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>{validateMessage}</WarnMessage>
      )}
    </InputWrapper>
  );
};

FormBlock.Radio = ({
  description,
  disabledOver = false,
  validateMessage,
  value,
  width,
  inline,
  ...props
}: FormRadioProps) => {
  const { isNewTheme, COLOR_THEME } = useTheme();
  return (
    <InputWrapper width={width}>
      <Description
        className={classNames({
          override: isNewTheme,
        })}
      >
        {description}
      </Description>
      <InputRadio
        isCssOverride={isNewTheme}
        value={value}
        className={classNames({
          warn: !isBlank(validateMessage),
          over: disabledOver && props.disabled,
          inline,
        })}
        {...props}
      />
      {validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>{validateMessage}</WarnMessage>
      )}
    </InputWrapper>
  );
};

FormBlock.Checkbox = ({
  description,
  descriptionColor,
  disabledOver = false,
  validateMessage,
  width,
  inline,
  maxWidth,
  fontWeight,
  fontSize,
  ...props
}: FormCheckboxProps) => {
  const { isNewTheme } = useTheme();
  return (
    <InputWrapper width={width} className={props.isCenter ? "center" : ""}>
      <Description
        className={classNames({
          override: isNewTheme,
        })}
        descriptionColor={descriptionColor}
        maxWidth={maxWidth}
        fontWeight={fontWeight}
        fontSize={fontSize}
      >
        {description}
      </Description>
      <Checkbox
        className={classNames({
          warn: !isBlank(validateMessage),
          over: disabledOver && props.disabled,
          inline,
        })}
        {...props}
      />
      {validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>{validateMessage}</WarnMessage>
      )}
    </InputWrapper>
  );
};
FormBlock.Checkbox2 = ({
  description,
  descriptionColor,
  disabledOver = false,
  validateMessage,
  width,
  inline,
  isBorder,
  ...props
}: FormCheckboxProps) => {
  const { isNewTheme } = useTheme();
  return (
    <InputWrapper width={width} className={props.isCenter ? "center" : ""}>
      <Checkbox
        checkboxColor={{
          text: "#333333",
          checkbox: {
            checked: "#0275ff",
            unchecked: "#f2f2f2",
          },
        }}
        className={classNames({
          warn: !isBlank(validateMessage),
          over: disabledOver && props.disabled,
          inline,
        })}
        isNewTheme={isNewTheme}
        labelFontSize="14px"
        labelfontWeight="400"
        {...props}
        isBorder={isBorder}
      />
      <Description
        className={classNames({
          checkbox2: true,
          addWidth: true,
        })}
        descriptionColor={descriptionColor}
      >
        {description}
      </Description>
      {validateMessage && (
        <WarnMessage className={`validate-warn ${isNewTheme ? "msgOverride" : ""}`}>{validateMessage}</WarnMessage>
      )}
    </InputWrapper>
  );
};

FormBlock.Row = ({ children, className, hidden }: FormBlockRowProps) => (
  <>{!hidden && <Row className={className}>{children}</Row>}</>
);

const StyledTypeSelect = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: row;
  ${breakpoint.lessThan("sm")`
  justify-content: space-around;
  `}
`;

const StyledOption = styled.div<{
  isActive: boolean;
  backgroundColor?: string;
}>`
  box-shadow: 0px 3px 6px #33333329;
  border-radius: 10px;
  opacity: 1;
  background-color: ${({ isActive, backgroundColor }) => (isActive ? backgroundColor : "#ffffff")};
  padding: 34px 30px 24px 30px;
  margin-left: 40px;
  width: 140px;
  height: auto;
  &: first-child {
    margin-left: 0;
  }
  ${breakpoint.lessThan("sm")`
  padding: 23px 0;
  margin-left: 0;
  width: 105px;
  `}
`;

const StyledDiv = styled.div`
  display: block;
  text-align: center;
`;

const StyledText = styled.div<{
  isActive: boolean;
}>`
  padding-top: 19px;
  font-size: 16px;
  text-align: center;
  color: ${({ isActive }) => (isActive ? "#ffffff" : "#333333")};
  font-weight: 700;
  ${breakpoint.lessThan("sm")`
  font-size: 14px;
  `}
`;

FormBlock.TypeSelect = (props: {
  options: { label: string; value: string }[];
  selected?: string;
  backgroundColor?: string;
  handleOptionSelect: (value: string) => void;
}) => {
  const { selected, backgroundColor, options, handleOptionSelect } = props;

  const getIconColor = (isActive: boolean): string => {
    return isActive ? "#ffffff" : "#333333";
  };

  const renderLogo = (value: string) => {
    switch (value) {
      case PropertyType.HOUSE:
        return <House fillColor={getIconColor(selected === value)} />;
      case PropertyType.LAND:
        return <Land fillColor={getIconColor(selected === value)} />;
      case PropertyType.MANSION:
        return <Mansion fillColor={getIconColor(selected === value)} />;
      default:
        return "";
    }
  };

  return (
    <StyledTypeSelect>
      {Array.isArray(options) &&
        options.map(option => (
          <StyledOption
            data-cy={`inquiry-property-type-${option.value}`}
            key={`inquiry-property-type-${option.value}`}
            isActive={selected === option.value}
            backgroundColor={backgroundColor}
            onClick={() => handleOptionSelect(option.value)}
          >
            <StyledDiv>{renderLogo(option.value)}</StyledDiv>
            <StyledText isActive={selected === option.value}>{option.label}</StyledText>
          </StyledOption>
        ))}
    </StyledTypeSelect>
  );
};

export default FormBlock;
